import { useState, useEffect, useRef } from 'react';
import { Plus, Users, Trash2, Download, XCircle, Shuffle, Minus, Database as DatabaseIcon, Settings as SettingsIcon, LogOut } from 'lucide-react';
import api from './api';
import { useAuth } from './AuthContext';

export default function App() {
  const { logout } = useAuth();
  const [participants, setParticipants] = useState([]);
  const [name, setName] = useState('');
  const [weight, setWeight] = useState('');
  const [weightClasses, setWeightClasses] = useState([]);
  const [minParticipants, setMinParticipants] = useState(3);
  const [maxParticipants, setMaxParticipants] = useState(5);
  const [draggedParticipant, setDraggedParticipant] = useState(null);
  const [touchDragging, setTouchDragging] = useState(false);
  const [touchTarget, setTouchTarget] = useState(null);
  const [useWeightRange, setUseWeightRange] = useState(false);
  const [maxWeightRange, setMaxWeightRange] = useState(15);
  const [bypassClasses, setBypassClasses] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Autocomplete state
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const nameInputRef = useRef(null);
  const suggestionsRef = useRef(null);
  
  // Participant management modal state
  const [showManageModal, setShowManageModal] = useState(false);
  const [allParticipantNames, setAllParticipantNames] = useState([]);

  // Load tournament data on mount
  useEffect(() => {
    loadTournamentData();
  }, []);

  // Auto-generate weight classes when participants or settings change
  useEffect(() => {
    if (participants.length > 0) {
      generateWeightClasses();
    } else {
      setWeightClasses([]);
    }
  }, [participants, minParticipants, maxParticipants, useWeightRange, maxWeightRange]);

  // Save settings to backend when they change
  useEffect(() => {
    if (!loading) {
      saveSettings();
    }
  }, [minParticipants, maxParticipants, useWeightRange, maxWeightRange]);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        nameInputRef.current &&
        !nameInputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadTournamentData = async () => {
    try {
      setLoading(true);
      const data = await api.getTournamentData();
      setParticipants(data.participants || []);
      
      if (data.settings) {
        setMinParticipants(data.settings.minParticipants || 3);
        setMaxParticipants(data.settings.maxParticipants || 5);
        setUseWeightRange(data.settings.useWeightRange || false);
        setMaxWeightRange(data.settings.maxWeightRange || 15);
      }
      
      setError(null);
    } catch (err) {
      setError('Fehler beim Laden der Turnierdaten');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      await api.updateSettings({
        minParticipants,
        maxParticipants,
        useWeightRange,
        maxWeightRange
      });
    } catch (err) {
      console.error('Error saving settings:', err);
    }
  };

  const handleNameChange = async (value) => {
    setName(value);
    setSelectedSuggestionIndex(-1);
    
    if (value.trim().length >= 1) {
      const suggestions = await api.searchParticipants(value.trim());
      setNameSuggestions(suggestions);
      setShowSuggestions(suggestions.length > 0);
    } else {
      setNameSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const selectSuggestion = (suggestion) => {
    setName(suggestion.name);
    setShowSuggestions(false);
    setNameSuggestions([]);
    // Focus on weight input
    document.querySelector('input[type="number"]')?.focus();
  };

  const handleNameKeyDown = (e) => {
    if (!showSuggestions || nameSuggestions.length === 0) {
      if (e.key === 'Enter') {
        e.preventDefault();
        addParticipant();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => 
          prev < nameSuggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedSuggestionIndex >= 0) {
          selectSuggestion(nameSuggestions[selectedSuggestionIndex]);
        } else {
          addParticipant();
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
        break;
      default:
        break;
    }
  };

  const addParticipant = async () => {
    const weightNum = parseFloat(weight);
    
    if (name.trim() && weight.trim() && !isNaN(weightNum) && weightNum > 0) {
      const participantName = name.trim();
      const participantWeight = weightNum;
      
      // Generate temporary ID for optimistic update
      const tempId = `temp-${Date.now()}`;
      const optimisticParticipant = {
        id: tempId,
        name: participantName,
        weight: participantWeight,
        participant_id: null
      };
      
      // Immediately update UI (optimistic update)
      setParticipants(prev => [...prev, optimisticParticipant]);
      setName('');
      setWeight('');
      setShowSuggestions(false);
      setNameSuggestions([]);
      setError(null);
      
      // Make API call in background
      try {
        const newParticipant = await api.addParticipant(participantName, participantWeight);
        
        // Replace temporary participant with real one from backend
        setParticipants(prev => 
          prev.map(p => p.id === tempId ? newParticipant : p)
        );
      } catch (err) {
        // Revert optimistic update on failure
        setParticipants(prev => prev.filter(p => p.id !== tempId));
        setError('Fehler beim Hinzufügen des Teilnehmers');
        console.error(err);
      }
    }
  };

  const removeParticipant = async (id) => {
    try {
      await api.removeParticipant(id);
      setParticipants(participants.filter(p => p.id !== id));
      setError(null);
    } catch (err) {
      setError('Fehler beim Entfernen des Teilnehmers');
      console.error(err);
    }
  };

  const clearAllParticipants = async () => {
    if (window.confirm('Möchten Sie wirklich alle Teilnehmer löschen? Diese Aktion kann nicht rückgängig gemacht werden.')) {
      try {
        await api.clearTournament();
        setParticipants([]);
        setBypassClasses({});
        setError(null);
      } catch (err) {
        setError('Fehler beim Löschen der Teilnehmer');
        console.error(err);
      }
    }
  };

  const resetApp = async () => {
    if (window.confirm('Möchten Sie ein neues Turnier starten? Das aktuelle Turnier wird beendet (Teilnehmer werden gespeichert).')) {
      try {
        await api.createNewTournament({
          minParticipants: 3,
          maxParticipants: 5,
          useWeightRange: false,
          maxWeightRange: 15
        });
        await loadTournamentData();
        setBypassClasses({});
        setError(null);
      } catch (err) {
        setError('Fehler beim Zurücksetzen der App');
        console.error(err);
      }
    }
  };

  const resortParticipants = () => {
    if (window.confirm('Möchten Sie die Gewichtsklassen neu sortieren? Alle manuellen Änderungen gehen verloren.')) {
      const hasBypassSettings = Object.values(bypassClasses).some(v => v === true);
      
      if (hasBypassSettings) {
        if (window.confirm('Sollen auch die aufgehobenen Limits zurückgesetzt werden?')) {
          setBypassClasses({});
        }
      }
      
      generateWeightClasses();
    }
  };

  const toggleBypassForClass = (classId) => {
    setBypassClasses(prev => ({
      ...prev,
      [classId]: !prev[classId]
    }));
  };

  const hasClassBypass = (classId) => {
    return bypassClasses[classId] === true;
  };

  const loadDemoData = async () => {
    const demoData = [
      { name: 'Leon', weight: 75 },
      { name: 'Mareike', weight: 50 },
      { name: 'Alina', weight: 55 },
      { name: 'David', weight: 80 },
      { name: 'Lukas', weight: 85 },
      { name: 'JJ', weight: 81 },
      { name: 'Alexander Wieczerzak', weight: 90 },
      { name: 'Michi', weight: 120 },
      { name: 'Malte', weight: 150 }
    ];

    if (participants.length > 0) {
      if (!window.confirm('Möchten Sie die Demo-Daten hinzufügen? Sie werden zu den bestehenden Teilnehmern hinzugefügt.')) {
        return;
      }
    }

    try {
      for (const participant of demoData) {
        const newParticipant = await api.addParticipant(participant.name, participant.weight);
        setParticipants(prev => [...prev, newParticipant]);
      }
      setError(null);
    } catch (err) {
      setError('Fehler beim Laden der Demo-Daten');
      console.error(err);
    }
  };

  const generateWeightClasses = () => {
    const sorted = [...participants].sort((a, b) => a.weight - b.weight);
    const classes = [];
    let i = 0;

    while (i < sorted.length) {
      let currentGroup = [sorted[i]];
      let j = i + 1;

      while (j < sorted.length && currentGroup.length < maxParticipants) {
        const weightSpread = sorted[j].weight - currentGroup[0].weight;
        
        if (useWeightRange) {
          if (weightSpread <= maxWeightRange) {
            currentGroup.push(sorted[j]);
            j++;
          } else {
            break;
          }
        } else {
          if (weightSpread <= 50 || currentGroup.length < minParticipants) {
            currentGroup.push(sorted[j]);
            j++;
          } else {
            break;
          }
        }
      }

      if (currentGroup.length < minParticipants && classes.length > 0 && i + currentGroup.length === sorted.length && !useWeightRange) {
        const lastClass = classes[classes.length - 1];
        if (lastClass.participants.length + currentGroup.length <= maxParticipants) {
          lastClass.participants.push(...currentGroup);
          lastClass.maxWeight = Math.max(...lastClass.participants.map(p => p.weight));
          lastClass.minWeight = Math.min(...lastClass.participants.map(p => p.weight));
          lastClass.avgWeight = lastClass.participants.reduce((sum, p) => sum + p.weight, 0) / lastClass.participants.length;
          break;
        }
      }

      classes.push({
        id: classes.length + 1,
        participants: [...currentGroup],
        minWeight: Math.min(...currentGroup.map(p => p.weight)),
        maxWeight: Math.max(...currentGroup.map(p => p.weight)),
        avgWeight: currentGroup.reduce((sum, p) => sum + p.weight, 0) / currentGroup.length
      });

      i = j;
    }

    const sortedClasses = classes.sort((a, b) => a.minWeight - b.minWeight);
    const renumberedClasses = sortedClasses.map((wc, index) => ({
      ...wc,
      id: index + 1
    }));

    setWeightClasses(renumberedClasses);
  };

  const handleDragStart = (e, participant, sourceClassId) => {
    setDraggedParticipant({ participant, sourceClassId });
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'move';
    }
  };

  const handleTouchStart = (e, participant, sourceClassId) => {
    setDraggedParticipant({ participant, sourceClassId });
    setTouchDragging(true);
    e.preventDefault();
  };

  const handleTouchMove = (e) => {
    if (!touchDragging || !draggedParticipant) return;
    e.preventDefault();
    
    const touch = e.touches[0];
    const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY);
    
    const dropZone = elementBelow?.closest('[data-drop-zone]');
    setTouchTarget(dropZone ? parseInt(dropZone.dataset.dropZone) : null);
  };

  const handleTouchEnd = (e) => {
    if (!touchDragging || !draggedParticipant) return;
    e.preventDefault();
    
    if (touchTarget) {
      moveParticipant(draggedParticipant.participant, draggedParticipant.sourceClassId, touchTarget);
    }
    
    setTouchDragging(false);
    setTouchTarget(null);
    setDraggedParticipant(null);
  };

  const moveParticipant = (participant, sourceClassId, targetClassId) => {
    if (sourceClassId === targetClassId) {
      return;
    }

    const targetClass = weightClasses.find(wc => wc.id === targetClassId);
    const targetHasBypass = hasClassBypass(targetClassId);
    
    if (targetClass && targetClass.participants.length >= maxParticipants && !targetHasBypass) {
      if (window.confirm(`Gewichtsklasse ${targetClassId} hat bereits die maximale Teilnehmerzahl erreicht (${maxParticipants} Teilnehmer).\n\nMöchten Sie das Limit für diese Klasse aufheben und den Teilnehmer trotzdem verschieben?`)) {
        setBypassClasses(prev => ({
          ...prev,
          [targetClassId]: true
        }));
      } else {
        return;
      }
    }

    setWeightClasses(prevClasses => {
      const updatedClasses = prevClasses.map(wc => {
        if (wc.id === sourceClassId) {
          const newParticipants = wc.participants.filter(p => p.id !== participant.id);
          if (newParticipants.length === 0) {
            return null;
          }
          return {
            ...wc,
            participants: newParticipants,
            minWeight: Math.min(...newParticipants.map(p => p.weight)),
            maxWeight: Math.max(...newParticipants.map(p => p.weight)),
            avgWeight: newParticipants.reduce((sum, p) => sum + p.weight, 0) / newParticipants.length
          };
        } else if (wc.id === targetClassId) {
          const newParticipants = [...wc.participants, participant];
          return {
            ...wc,
            participants: newParticipants,
            minWeight: Math.min(...newParticipants.map(p => p.weight)),
            maxWeight: Math.max(...newParticipants.map(p => p.weight)),
            avgWeight: newParticipants.reduce((sum, p) => sum + p.weight, 0) / newParticipants.length
          };
        }
        return wc;
      }).filter(wc => wc !== null);
      
      const sortedClasses = updatedClasses.sort((a, b) => a.minWeight - b.minWeight);
      return sortedClasses.map((wc, index) => ({
        ...wc,
        id: index + 1
      }));
    });
  };

  const handleDrop = (e, targetClassId) => {
    e.preventDefault();
    if (!draggedParticipant) return;

    const { participant, sourceClassId } = draggedParticipant;
    moveParticipant(participant, sourceClassId, targetClassId);
    setDraggedParticipant(null);
  };

  const exportData = () => {
    const data = {
      participants,
      weightClasses: weightClasses.map(wc => ({
        class: `Gewichtsklasse ${wc.id}`,
        weightRange: `${wc.minWeight.toFixed(1)} - ${wc.maxWeight.toFixed(1)} kg`,
        participants: wc.participants.map(p => `${p.name} (${p.weight}kg)`).join(', ')
      }))
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'judo-turnier-plan.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const openManageModal = async () => {
    const names = await api.getAllParticipants();
    setAllParticipantNames(names);
    setShowManageModal(true);
  };

  const deleteParticipantName = async (id) => {
    if (window.confirm('Möchten Sie diesen Namen dauerhaft löschen?')) {
      try {
        await api.deleteParticipant(id);
        setAllParticipantNames(allParticipantNames.filter(p => p.id !== id));
      } catch (err) {
        setError('Fehler beim Löschen des Namens');
        console.error(err);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-indigo-600 font-medium">Lade Turnierdaten...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-6xl mx-auto">
        <header className="text-center mb-8">
          <div className="flex justify-between items-start mb-4">
            <div className="flex-1"></div>
            <div className="flex-1 text-center">
              <h1 className="text-4xl font-bold text-indigo-900 mb-2">Judo Turnier Planer</h1>
              <p className="text-indigo-600">Erstelle optimale Gewichtsklassen für dein Turnier</p>
            </div>
            <div className="flex-1 flex justify-end">
              <button
                onClick={logout}
                className="text-gray-600 hover:text-gray-800 flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors"
                title="Abmelden"
              >
                <LogOut size={20} />
                <span className="text-sm font-medium">Abmelden</span>
              </button>
            </div>
          </div>
          {error && (
            <div className="mt-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
              {error}
            </div>
          )}
        </header>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Participant Entry */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-semibold text-gray-800 flex items-center">
                <Plus className="mr-2 text-indigo-600" size={24} />
                Teilnehmer hinzufügen
              </h2>
              <button
                onClick={openManageModal}
                className="text-indigo-600 hover:text-indigo-800 text-sm font-medium flex items-center"
                title="Namen verwalten"
              >
                <SettingsIcon size={16} className="mr-1" />
                Namen verwalten
              </button>
            </div>
            
            <div className="mb-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="relative">
                  <input
                    ref={nameInputRef}
                    type="text"
                    placeholder="Name des Teilnehmers"
                    value={name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    onKeyDown={handleNameKeyDown}
                    onFocus={() => name.trim().length >= 1 && setShowSuggestions(nameSuggestions.length > 0)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                  {showSuggestions && nameSuggestions.length > 0 && (
                    <div
                      ref={suggestionsRef}
                      className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                    >
                      {nameSuggestions.map((suggestion, index) => (
                        <div
                          key={suggestion.id}
                          onClick={() => selectSuggestion(suggestion)}
                          className={`px-4 py-2 cursor-pointer hover:bg-indigo-50 ${
                            index === selectedSuggestionIndex ? 'bg-indigo-100' : ''
                          }`}
                        >
                          <div className="font-medium text-gray-800">{suggestion.name}</div>
                          <div className="text-xs text-gray-500">
                            Zuletzt verwendet: {new Date(suggestion.last_used).toLocaleDateString('de-DE')}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <input
                  type="number"
                  placeholder="Gewicht (kg)"
                  step="0.1"
                  min="0"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addParticipant();
                    }
                  }}
                />
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    addParticipant();
                  }}
                  className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition-colors font-medium flex items-center justify-center"
                >
                  <Plus size={20} className="mr-1" />
                  Hinzufügen
                </button>
              </div>
            </div>

            {/* Participants List */}
            <div className="space-y-2 max-h-96 overflow-y-auto">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-medium text-gray-700">
                  Teilnehmer ({participants.length})
                </h3>
                {participants.length > 0 && (
                  <button
                    onClick={clearAllParticipants}
                    className="text-red-600 hover:text-red-800 text-sm font-medium flex items-center transition-colors"
                  >
                    <XCircle size={16} className="mr-1" />
                    Alle löschen
                  </button>
                )}
              </div>
              {participants.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Noch keine Teilnehmer hinzugefügt</p>
              ) : (
                participants
                  .sort((a, b) => a.weight - b.weight)
                  .map((participant) => (
                    <div
                      key={participant.id}
                      className="flex justify-between items-center bg-gray-50 p-3 rounded-lg"
                    >
                      <span className="font-medium text-gray-800">
                        {participant.name}
                      </span>
                      <div className="flex items-center space-x-3">
                        <span className="text-indigo-600 font-semibold">
                          {participant.weight} kg
                        </span>
                        <button
                          onClick={() => removeParticipant(participant.id)}
                          className="text-red-500 hover:text-red-700 transition-colors"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  ))
              )}
            </div>
          </div>

          {/* Weight Classes */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-semibold text-gray-800 flex items-center">
                <Users className="mr-2 text-indigo-600" size={24} />
                Gewichtsklassen ({weightClasses.length})
              </h2>
              {weightClasses.length > 0 && (
                <div className="flex space-x-2">
                  <button
                    onClick={resortParticipants}
                    className="bg-indigo-600 text-white p-2 rounded-lg hover:bg-indigo-700 transition-colors flex items-center"
                    title="Gewichtsklassen neu sortieren"
                  >
                    <Shuffle size={18} />
                  </button>
                  <button
                    onClick={exportData}
                    className="bg-green-600 text-white p-2 rounded-lg hover:bg-green-700 transition-colors flex items-center"
                    title="Daten exportieren"
                  >
                    <Download size={18} />
                  </button>
                </div>
              )}
            </div>

            {/* Settings */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <div className="flex flex-wrap items-center gap-4 mb-3">
                <div className="flex items-center space-x-2">
                  <label className="text-sm font-medium text-gray-600">
                    Min:
                  </label>
                  <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setMinParticipants(Math.max(2, minParticipants - 1))}
                      className="p-1 bg-gray-100 hover:bg-gray-200 transition-colors"
                      disabled={minParticipants <= 2}
                    >
                      <Minus size={14} />
                    </button>
                    <span className="px-3 py-1 text-sm font-medium bg-white min-w-[2rem] text-center">
                      {minParticipants}
                    </span>
                    <button
                      onClick={() => setMinParticipants(Math.min(10, minParticipants + 1))}
                      className="p-1 bg-gray-100 hover:bg-gray-200 transition-colors"
                      disabled={minParticipants >= 10}
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <label className="text-sm font-medium text-gray-600">
                    Max:
                  </label>
                  <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setMaxParticipants(Math.max(2, maxParticipants - 1))}
                      className="p-1 bg-gray-100 hover:bg-gray-200 transition-colors"
                      disabled={maxParticipants <= 2}
                    >
                      <Minus size={14} />
                    </button>
                    <span className="px-3 py-1 text-sm font-medium bg-white min-w-[2rem] text-center">
                      {maxParticipants}
                    </span>
                    <button
                      onClick={() => setMaxParticipants(Math.min(10, maxParticipants + 1))}
                      className="p-1 bg-gray-100 hover:bg-gray-200 transition-colors"
                      disabled={maxParticipants >= 10}
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                </div>
                <span className="text-xs text-gray-500">Teilnehmer pro Klasse</span>
              </div>
              
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="useWeightRange"
                    checked={useWeightRange}
                    onChange={(e) => setUseWeightRange(e.target.checked)}
                    className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  <label htmlFor="useWeightRange" className="text-sm font-medium text-gray-600">
                    Gewichtsspanne pro Klasse begrenzen
                  </label>
                </div>
                {useWeightRange && (
                  <div className="flex items-center space-x-2">
                    <label className="text-sm font-medium text-gray-600">
                      Max. Spanne:
                    </label>
                    <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
                      <button
                        onClick={() => setMaxWeightRange(Math.max(1, maxWeightRange - 1))}
                        className="p-1 bg-gray-100 hover:bg-gray-200 transition-colors"
                        disabled={maxWeightRange <= 1}
                      >
                        <Minus size={14} />
                      </button>
                      <span className="px-3 py-1 text-sm font-medium bg-white min-w-[2.5rem] text-center">
                        {maxWeightRange}
                      </span>
                      <button
                        onClick={() => setMaxWeightRange(Math.min(50, maxWeightRange + 1))}
                        className="p-1 bg-gray-100 hover:bg-gray-200 transition-colors"
                        disabled={maxWeightRange >= 50}
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                    <span className="text-xs text-gray-500">kg</span>
                  </div>
                )}
              </div>
              
              {weightClasses.length > 0 && (
                <p className="text-xs text-gray-500 mt-2 italic">
                  💡 Tipp: Teilnehmer per Drag & Drop zwischen Gewichtsklassen verschieben. Auf Touch-Geräten: Tippen und halten, dann zur Zielklasse ziehen.
                </p>
              )}
            </div>

            <div className="space-y-4 max-h-96 overflow-y-auto">
              {weightClasses.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  Teilnehmer hinzufügen um Gewichtsklassen zu sehen
                </p>
              ) : (
                weightClasses.map((weightClass) => (
                  <div
                    key={weightClass.id}
                    className={`border rounded-lg p-4 bg-gradient-to-r ${
                      hasClassBypass(weightClass.id) 
                        ? 'from-amber-50 to-yellow-50 border-amber-300' 
                        : 'from-indigo-50 to-blue-50 border-gray-200'
                    } ${
                      touchTarget === weightClass.id ? 'ring-2 ring-indigo-400 bg-indigo-100' : ''
                    }`}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, weightClass.id)}
                    data-drop-zone={weightClass.id}
                  >
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-lg font-semibold text-indigo-800">
                        Gewichtsklasse {weightClass.id}
                        {hasClassBypass(weightClass.id) && (
                          <span className="ml-2 text-xs font-normal text-amber-600 bg-amber-100 px-2 py-0.5 rounded">
                            Limit aufgehoben
                          </span>
                        )}
                      </h3>
                      <div className="flex items-center space-x-2">
                        <label className="flex items-center space-x-1 text-xs text-gray-500 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={hasClassBypass(weightClass.id)}
                            onChange={() => toggleBypassForClass(weightClass.id)}
                            className="w-3 h-3 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                          />
                          <span>Kein Limit</span>
                        </label>
                        <span className={`text-sm px-2 py-1 rounded ${
                          weightClass.participants.length > maxParticipants 
                            ? 'bg-amber-100 text-amber-700' 
                            : 'bg-white text-gray-600'
                        }`}>
                          {weightClass.participants.length} Kämpfer
                          {weightClass.participants.length > maxParticipants && (
                            <span className="ml-1 text-xs">(+{weightClass.participants.length - maxParticipants})</span>
                          )}
                        </span>
                      </div>
                    </div>
                    
                    <div className="text-sm text-gray-600 mb-3">
                      <span className="font-medium">Bereich:</span> {weightClass.minWeight.toFixed(1)} - {weightClass.maxWeight.toFixed(1)} kg
                      <span className="ml-4 font-medium">∅:</span> {weightClass.avgWeight.toFixed(1)} kg
                    </div>

                    <div className="space-y-1">
                      {weightClass.participants
                        .sort((a, b) => a.weight - b.weight)
                        .map((participant) => (
                        <div
                          key={participant.id}
                          draggable="true"
                          onDragStart={(e) => handleDragStart(e, participant, weightClass.id)}
                          onTouchStart={(e) => handleTouchStart(e, participant, weightClass.id)}
                          onTouchMove={handleTouchMove}
                          onTouchEnd={handleTouchEnd}
                          className={`flex justify-between items-center bg-white p-2 rounded text-sm cursor-move hover:bg-gray-50 border border-transparent hover:border-indigo-200 transition-all ${
                            touchDragging && draggedParticipant?.participant.id === participant.id ? 'opacity-50 scale-95' : ''
                          }`}
                          style={{ touchAction: 'none' }}
                        >
                          <span className="font-medium flex items-center">
                            <span className="text-gray-400 mr-2">⋮⋮</span>
                            {participant.name}
                          </span>
                          <span className="text-indigo-600 font-semibold">
                            {participant.weight} kg
                          </span>
                        </div>
                      ))}
                      
                      <div className="h-8 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-xs text-gray-400 opacity-0 hover:opacity-100 transition-opacity">
                        Teilnehmer hier ablegen
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {participants.length > 0 && (
          <div className="mt-8 bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Turnier Übersicht</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
              <div className="bg-indigo-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-indigo-600">{participants.length}</div>
                <div className="text-gray-600">Teilnehmer gesamt</div>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{weightClasses.length}</div>
                <div className="text-gray-600">Gewichtsklassen</div>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-purple-600">
                  {weightClasses.length > 0 ? 
                    (weightClasses.reduce((sum, wc) => sum + wc.participants.length, 0) / weightClasses.length).toFixed(1) 
                    : '0'}
                </div>
                <div className="text-gray-600">∅ pro Klasse</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Demo Data and Reset Buttons */}
      <div className="mt-8 text-center space-y-3">
        <button
          onClick={loadDemoData}
          className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center mx-auto text-sm font-medium shadow-lg"
        >
          <DatabaseIcon size={18} className="mr-2" />
          Demo-Daten hinzufügen
        </button>
        <button
          onClick={resetApp}
          className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center mx-auto text-sm font-medium shadow-lg"
        >
          <XCircle size={18} className="mr-2" />
          Neues Turnier starten
        </button>
      </div>

      {/* Participant Management Modal */}
      {showManageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-2xl font-semibold text-gray-800">Namen verwalten</h3>
                <button
                  onClick={() => setShowManageModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <XCircle size={24} />
                </button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {allParticipantNames.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Keine gespeicherten Namen</p>
              ) : (
                <div className="space-y-2">
                  {allParticipantNames.map((participant) => (
                    <div
                      key={participant.id}
                      className="flex justify-between items-center bg-gray-50 p-3 rounded-lg hover:bg-gray-100"
                    >
                      <div>
                        <div className="font-medium text-gray-800">{participant.name}</div>
                        <div className="text-xs text-gray-500">
                          Zuletzt verwendet: {new Date(participant.last_used).toLocaleDateString('de-DE')}
                        </div>
                      </div>
                      <button
                        onClick={() => deleteParticipantName(participant.id)}
                        className="text-red-500 hover:text-red-700 transition-colors"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}